import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Trash2, ChevronDown, ChevronRight, ArrowLeft, Zap, FileText, Upload, Download, AlertCircle } from 'react-feather';
import { apiClient } from '../shared/api/client';
import { Transaction, REIMBURSABLE_CATEGORIES } from '../shared/types';
import { parseAmount } from '../shared/utils/amount';

interface RuleInfo {
  id: number;
  description: string;
  textMatch: string | null;
  amountMin: number | null;
  amountMax: number | null;
  accountId: number | null;
  category: string | null;
  categoryColor: string | null;
  renameTo: string | null;
  priority: number;
  isActive: boolean;
  isRecurring: boolean;
  intervalMonths: number | null;
}

interface CategorizeModalProps {
  tx: Transaction;
  onClose: () => void;
  // Kept for compatibility with existing parents; the modal now stays open
  // after saves and reports changes via onDataChanged instead
  onSaved?: (notice: string) => void;
  // Called whenever data changed so the page behind can refresh
  onDataChanged?: () => void;
}

// The transaction modal: an overview "dashboard" showing what is currently
// set (category, name, split, tags, netting, rules), with sub-views to edit
// each part. Saves return to the overview instead of closing the modal.
type ModalView = 'overview' | 'category' | 'name' | 'split' | 'tags' | 'refund' | 'rules' | 'receipt';

const VIEW_TITLES: Record<Exclude<ModalView, 'overview'>, string> = {
  category: 'Kategori',
  name: 'Namn',
  split: 'Dela upp',
  tags: 'Taggar',
  refund: 'Kvittning',
  rules: 'Regler',
  receipt: 'Kvitto',
};

const CategorizeModal: React.FC<CategorizeModalProps> = ({ tx, onClose, onDataChanged }) => {
  const [view, setView] = useState<ModalView>('overview');
  const [receipt, setReceipt] = useState<any | null>(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const receiptInput = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<{ id: number; name: string; color?: string }[]>([]);
  const [categoryTree, setCategoryTree] = useState<{ id: number; name: string; color?: string; children: { id: number; name: string; color?: string }[] }[]>([]);
  const [expandedCatGroups, setExpandedCatGroups] = useState<Set<number>>(new Set());
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatParentId, setNewCatParentId] = useState<string>('');
  const [creatingCat, setCreatingCat] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(tx.category?.id || null);
  const [curCategory, setCurCategory] = useState<{ name: string; color?: string } | null>(
    tx.category ? { name: tx.category.name, color: tx.category.color } : null
  );
  const [createRule, setCreateRule] = useState(!tx.category);
  const [ruleText, setRuleText] = useState((tx.rawDescription || tx.description).toLowerCase());
  const [renameTo, setRenameTo] = useState('');
  const [editDescription, setEditDescription] = useState(tx.description);
  const [displayName, setDisplayName] = useState(tx.description);
  const [saving, setSaving] = useState(false);
  const [allRules, setAllRules] = useState<RuleInfo[]>([]);
  const [savedSplitCount, setSavedSplitCount] = useState((tx.splits || []).length);
  const [splitParts, setSplitParts] = useState<{ categoryId: string; amount: string; label: string }[]>(
    (tx.splits || []).length > 0
      ? tx.splits!.map(s => ({ categoryId: String(s.category.id), amount: String(s.amount), label: s.label || '' }))
      : [
          { categoryId: tx.category ? String(tx.category.id) : '', amount: String(Math.abs(tx.amount)), label: '' },
          { categoryId: '', amount: '', label: '' },
        ]
  );
  const [allTags, setAllTags] = useState<{ id: number; name: string; color?: string }[]>([]);
  const [txTagIds, setTxTagIds] = useState<number[]>((tx.tags || []).map(t => t.id));
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  // Refund linking (income transactions only): pick the purchase this
  // return nets against
  const isIncome = tx.amount > 0;
  const [refundTargetId, setRefundTargetId] = useState<number | null>(tx.refundsTransactionId || null);
  const [refundTargetLabel, setRefundTargetLabel] = useState<string | null>(tx.refundsTargetName || null);
  const [showRefundPicker, setShowRefundPicker] = useState(false);
  const [refundSearch, setRefundSearch] = useState('');
  const [allTxs, setAllTxs] = useState<Transaction[]>([]);
  // Expense side: returns already linked to this purchase + linking state
  const [linkedRefunds, setLinkedRefunds] = useState(tx.refundItems || []);
  const [linkingRefund, setLinkingRefund] = useState(false);
  // Money moved between own accounts is neither income nor spending. The
  // automatic detection only sees accounts in the bank connection, so ISK
  // and fund accounts have to be marked by hand.
  const [isTransfer, setIsTransfer] = useState(!!tx.isTransfer);
  const [savingTransfer, setSavingTransfer] = useState(false);
  // Close only when both mousedown AND mouseup land on the backdrop -
  // dragging a text selection out of the modal must not close it
  const backdropMouseDown = useRef(false);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' kr';

  const loadRules = () =>
    apiClient.get('/rules')
      .then(response => setAllRules(((response as any).rules || []) as RuleInfo[]))
      .catch(err => console.error('Failed to load rules:', err));

  useEffect(() => {
    loadTags();
    loadCategoryTree().then(tree => {
      if (tx.category) {
        const parent = tree.find((t: any) => t.children.some((c: any) => c.id === tx.category!.id));
        if (parent) setExpandedCatGroups(prev => new Set(prev).add(parent.id));
      }
    });
    apiClient.get('/transactions?limit=300')
      .then(response => {
        setAllTxs((((response as any).transactions || []) as Transaction[]).filter(t => t.id !== tx.id));
      })
      .catch(err => console.error('Failed to load transactions for refund matching:', err));
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rules that hit this transaction, mirroring the server-side rule engine:
  // text match against the BANK description, amount bounds, account filter.
  // The engine picks the first match by priority for category and rename, so
  // keep that order here too.
  const bankText = (tx.rawDescription || tx.description).toLowerCase();
  const matchingRules = allRules
    .filter(r => r.isActive)
    .filter(r => r.textMatch || r.amountMin !== null || r.amountMax !== null)
    .filter(r => !r.textMatch || bankText.includes(r.textMatch.toLowerCase()))
    .filter(r => r.amountMin === null || Math.abs(tx.amount) >= r.amountMin)
    .filter(r => r.amountMax === null || Math.abs(tx.amount) <= r.amountMax)
    .filter(r => !r.accountId || !tx.account || tx.account.id === r.accountId)
    // The server tries rules in priority order and lets the first match win
    .sort((a, b) => a.priority - b.priority || a.id - b.id);
  const effectiveCategoryRuleId = matchingRules.find(r => r.category)?.id;
  const effectiveRenameRuleId = matchingRules.find(r => r.renameTo)?.id;

  const recentExpenses = allTxs.filter(t => t.type === 'EXPENSE' && !t.isTransfer);

  // Refund suggestions for an expense: incomes after the purchase date with
  // the exact same amount or a matching merchant prefix
  const normalizeName = (s: string) => s.replace(/^K\*/i, '').replace(/[^a-zåäö0-9]/gi, '').toUpperCase().slice(0, 6);
  const refundSuggestions = !isIncome
    ? allTxs
        .filter(t => t.type === 'INCOME' && !t.isTransfer && !t.refundsTransactionId)
        .filter(t => !t.date || !tx.date || new Date(t.date) >= new Date(tx.date))
        .map(t => ({
          t,
          score: (Math.abs(Math.abs(t.amount) - Math.abs(tx.amount)) < 0.01 ? 2 : 0)
            + (normalizeName(t.rawDescription || t.description) === normalizeName(tx.rawDescription || tx.description) ? 1 : 0)
        }))
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score || a.t.date.localeCompare(b.t.date))
        .slice(0, 3)
        .map(s => s.t)
    : [];

  const linkRefund = async (income: Transaction) => {
    try {
      setLinkingRefund(true);
      await apiClient.put(`/transactions/${income.id}`, { refundsTransactionId: tx.id });
      setLinkedRefunds(prev => [...prev, { id: income.id, amount: Math.abs(income.amount), date: income.date, name: income.description }]);
      setAllTxs(prev => prev.map(t => t.id === income.id ? { ...t, refundsTransactionId: tx.id } : t));
      onDataChanged?.();
    } catch (err) {
      console.error('Failed to link refund:', err);
    } finally {
      setLinkingRefund(false);
    }
  };

  const unlinkRefund = async (incomeId: number) => {
    try {
      setLinkingRefund(true);
      await apiClient.put(`/transactions/${incomeId}`, { refundsTransactionId: null });
      setLinkedRefunds(prev => prev.filter(r => r.id !== incomeId));
      setAllTxs(prev => prev.map(t => t.id === incomeId ? { ...t, refundsTransactionId: null } : t));
      onDataChanged?.();
    } catch (err) {
      console.error('Failed to unlink refund:', err);
    } finally {
      setLinkingRefund(false);
    }
  };

  // Income side: net THIS income against a purchase — saved immediately,
  // same behavior as the expense side
  const linkToPurchase = async (expense: Transaction) => {
    try {
      setLinkingRefund(true);
      await apiClient.put(`/transactions/${tx.id}`, { refundsTransactionId: expense.id });
      setRefundTargetId(expense.id);
      setRefundTargetLabel(expense.description);
      onDataChanged?.();
    } catch (err) {
      console.error('Failed to link to purchase:', err);
    } finally {
      setLinkingRefund(false);
    }
  };

  const unlinkFromPurchase = async () => {
    try {
      setLinkingRefund(true);
      await apiClient.put(`/transactions/${tx.id}`, { refundsTransactionId: null });
      setRefundTargetId(null);
      setRefundTargetLabel(null);
      onDataChanged?.();
    } catch (err) {
      console.error('Failed to unlink from purchase:', err);
    } finally {
      setLinkingRefund(false);
    }
  };

  const toggleTransfer = async () => {
    const next = !isTransfer;
    try {
      setSavingTransfer(true);
      setIsTransfer(next);
      await apiClient.put(`/transactions/${tx.id}`, { isTransfer: next });
      setNotice(next
        ? 'Markerad som överföring — räknas inte längre som inkomst eller utgift.'
        : 'Markeringen borttagen — räknas igen i statistiken.');
      onDataChanged?.();
    } catch (err) {
      console.error('Failed to toggle transfer:', err);
      setIsTransfer(!next);
      setNotice('Kunde inte spara. Försök igen.');
    } finally {
      setSavingTransfer(false);
    }
  };

  const loadTags = async () => {
    try {
      const response = await apiClient.get('/tags');
      setAllTags((response as any).tags || []);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  };

  // Tags save immediately on toggle — the overview reflects the change
  const saveTags = async (next: number[]) => {
    const previous = txTagIds;
    setTxTagIds(next);
    try {
      await apiClient.put(`/transactions/${tx.id}`, { tagIds: next });
      onDataChanged?.();
    } catch (err) {
      console.error('Failed to save tags:', err);
      setTxTagIds(previous);
    }
  };

  const toggleTag = (tagId: number) => {
    saveTags(txTagIds.includes(tagId) ? txTagIds.filter(id => id !== tagId) : [...txTagIds, tagId]);
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const response = await apiClient.post('/tags', { name: newTagName.trim() });
      const tagId = (response as any).tagId;
      await loadTags();
      if (tagId && !txTagIds.includes(tagId)) await saveTags([...txTagIds, tagId]);
      setNewTagName('');
      setShowNewTag(false);
    } catch (err) {
      console.error('Failed to create tag:', err);
    }
  };

  const loadCategoryTree = async () => {
    try {
      const response = await apiClient.get('/categories');
      const tree = ((response as any).categories || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        color: c.color,
        children: (c.children || []).map((ch: any) => ({ id: ch.id, name: ch.name, color: ch.color })),
      }));
      setCategoryTree(tree);
      const flat = tree.flatMap((c: any) => [
        { id: c.id, name: c.name, color: c.color },
        ...c.children.map((ch: any) => ({ id: ch.id, name: `${c.name} › ${ch.name}`, color: ch.color })),
      ]);
      setCategoryOptions(flat);
      return tree;
    } catch (err) {
      console.error('Failed to load categories:', err);
      return [];
    }
  };

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      setCreatingCat(true);
      const response = await apiClient.post('/categories', {
        name: newCatName.trim(),
        color: '#64748B',
        parentId: newCatParentId ? parseInt(newCatParentId) : null,
      });
      const newId = (response as any).categoryId;
      await loadCategoryTree();
      if (newId) {
        setSelectedCategoryId(newId);
        if (newCatParentId) setExpandedCatGroups(prev => new Set(prev).add(parseInt(newCatParentId)));
      }
      setShowNewCategory(false);
      setNewCatName('');
      setNewCatParentId('');
    } catch (err) {
      console.error('Failed to create category:', err);
    } finally {
      setCreatingCat(false);
    }
  };

  const canSaveCategory = createRule
    ? !!ruleText.trim() && (!!selectedCategoryId || !!renameTo.trim())
    : !!selectedCategoryId;

  const canSaveName = editDescription.trim() !== '' && editDescription.trim() !== displayName;

  // Split helpers: every part needs a category and a positive amount, and
  // the parts must add up to the full transaction amount
  const splitTotal = Math.abs(tx.amount);
  const splitSum = splitParts.reduce((s, p) => s + (parseAmount(p.amount) || 0), 0);
  const splitRemainder = Math.round((splitTotal - splitSum) * 100) / 100;
  const splitPartsValid = splitParts.length >= 2 &&
    splitParts.every(p => p.categoryId && (parseAmount(p.amount) || 0) > 0);
  const canSaveSplit = splitPartsValid && Math.abs(splitRemainder) <= 0.01;

  const handleSaveCategory = async () => {
    if (!canSaveCategory) return;
    const categoryName = selectedCategoryId
      ? (categoryOptions.find(c => c.id === selectedCategoryId)?.name || '')
      : '';
    try {
      setSaving(true);
      if (createRule && ruleText.trim()) {
        const response = await apiClient.post('/rules', {
          description: selectedCategoryId
            ? `${renameTo.trim() || ruleText.trim()} → ${categoryName}`
            : `Namn: ${renameTo.trim()}`,
          textMatch: ruleText.trim(),
          categoryIds: selectedCategoryId ? [selectedCategoryId] : [],
          tagIds: txTagIds,
          priority: 3,
          renameTo: renameTo.trim() || null,
        });
        const categorized = (response as any).categorized || 0;
        setNotice(selectedCategoryId
          ? `Regel skapad — ${categorized} liknande transaktion${categorized !== 1 ? 'er' : ''} kategoriserades som ${categoryName}.`
          : `Regel skapad — alla matchande transaktioner döps till "${renameTo.trim()}".`);
        if (renameTo.trim()) {
          setDisplayName(renameTo.trim());
          setEditDescription(renameTo.trim());
        }
        loadRules();
      } else if (selectedCategoryId) {
        await apiClient.put(`/transactions/${tx.id}`, { categoryId: selectedCategoryId });
        setNotice(`Kategoriserad som ${categoryName}.`);
      }
      if (selectedCategoryId) {
        const opt = categoryOptions.find(c => c.id === selectedCategoryId);
        if (opt) setCurCategory({ name: opt.name, color: opt.color });
      }
      onDataChanged?.();
      setView('overview');
    } catch (err) {
      console.error('Failed to categorize:', err);
      setNotice('Kunde inte spara kategoriseringen. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!canSaveName) return;
    try {
      setSaving(true);
      await apiClient.put(`/transactions/${tx.id}`, { displayName: editDescription.trim() });
      setDisplayName(editDescription.trim());
      setNotice('Namnet sparat.');
      onDataChanged?.();
      setView('overview');
    } catch (err) {
      console.error('Failed to save name:', err);
      setNotice('Kunde inte spara namnet. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSplit = async () => {
    if (!canSaveSplit) return;
    try {
      setSaving(true);
      await apiClient.put(`/transactions/${tx.id}/splits`, {
        parts: splitParts.map(p => ({
          categoryId: parseInt(p.categoryId),
          amount: parseAmount(p.amount) || 0,
          label: p.label.trim() || null,
        })),
      });
      setSavedSplitCount(splitParts.length);
      setNotice(`Transaktionen delades upp i ${splitParts.length} delar.`);
      onDataChanged?.();
      setView('overview');
    } catch (err) {
      console.error('Failed to save split:', err);
      setNotice('Kunde inte spara uppdelningen. Kontrollera att delarna summerar till hela beloppet.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSplit = async () => {
    try {
      setSaving(true);
      await apiClient.delete(`/transactions/${tx.id}/splits`);
      setSavedSplitCount(0);
      setNotice('Uppdelningen togs bort — transaktionen ligger kvar i sin huvudkategori.');
      onDataChanged?.();
      setView('overview');
    } catch (err) {
      console.error('Failed to remove split:', err);
      setNotice('Kunde inte ta bort uppdelningen.');
    } finally {
      setSaving(false);
    }
  };

  const refundSummary = isIncome
    ? (refundTargetId ? `Kvittas mot ${refundTargetLabel || 'valt köp'}` : null)
    : (linkedRefunds.length > 0
        ? `+${formatAmount(linkedRefunds.reduce((s, r) => s + r.amount, 0))} · ${linkedRefunds.length} inbetalning${linkedRefunds.length !== 1 ? 'ar' : ''}`
        : null);

  // Receipts belong to work expenses, and uploading one used to be possible
  // only from the receipts page in settings - not where anyone stands when
  // they are looking at the charge itself.
  const isWorkExpense = tx.type === 'EXPENSE' && !tx.isTransfer && !!tx.category
    && REIMBURSABLE_CATEGORIES.includes(tx.category.name);

  useEffect(() => {
    if (!isWorkExpense) return;
    let cancelled = false;
    apiClient.get(`/mail/receipts/by-transaction/${tx.id}`)
      .then((res: any) => { if (!cancelled) setReceipt(res.receipt || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tx.id, isWorkExpense]);

  const uploadReceipt = async (file: File) => {
    setReceiptError(null); setReceiptBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('transactionId', String(tx.id));
      form.append('vendor', displayName || tx.description);
      await apiClient.postForm('/mail/receipts/upload', form);
      const res: any = await apiClient.get(`/mail/receipts/by-transaction/${tx.id}`);
      setReceipt(res.receipt || null);
      setNotice('Kvittot sparades.');
      onDataChanged?.();
    } catch (e: any) {
      setReceiptError(e?.response?.data?.error || 'Kunde inte ladda upp filen.');
    } finally { setReceiptBusy(false); }
  };

  const downloadReceipt = async () => {
    if (!receipt?.hasFile) return;
    setReceiptError(null);
    try {
      const blob = await apiClient.getBlob(`/mail/receipts/${receipt.id}/file`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = receipt.filename || `kvitto-${receipt.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setReceiptError('Kunde inte hämta filen.');
    }
  };

  const overviewRow = (label: string, targetView: ModalView, value: React.ReactNode) => (
    <button
      onClick={() => { setNotice(null); setView(targetView); }}
      className="w-full flex items-center py-3 px-2 text-left hover:bg-gray-50 rounded-lg border-b border-gray-100 last:border-0"
    >
      <span className="text-sm text-gray-500 w-28 flex-shrink-0">{label}</span>
      <span className="flex-1 flex items-center justify-end gap-1.5 min-w-0 text-sm font-medium text-gray-800 text-right">
        {value}
      </span>
      <ChevronRight size={16} className="text-gray-300 ml-2 flex-shrink-0" />
    </button>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
      onMouseDown={e => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onMouseUp={e => {
        if (backdropMouseDown.current && e.target === e.currentTarget && !saving) {
          onClose();
        }
        backdropMouseDown.current = false;
      }}
    >
      <div className="bg-white rounded-xl p-5 w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {view === 'overview' ? (
          <h2 className="text-lg font-bold text-gray-900 mb-1">Transaktion</h2>
        ) : (
          <div className="flex items-center mb-1">
            <button
              onClick={() => setView('overview')}
              className="p-1 -ml-1 mr-2 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft size={18} />
            </button>
            <h2 className="text-lg font-bold text-gray-900">{VIEW_TITLES[view]}</h2>
          </div>
        )}
        <p className="text-sm text-gray-600 mb-1">
          {displayName} · {formatAmount(Math.abs(tx.amount))}
          {tx.date ? ` · ${new Date(tx.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}` : ''}
        </p>
        {tx.rawDescription && tx.rawDescription !== displayName ? (
          <p className="text-xs text-gray-400 mb-3">Bankens text: {tx.rawDescription}</p>
        ) : (
          <div className="mb-3"></div>
        )}

        {view === 'overview' && (
          <>
            <div className="flex-1 overflow-y-auto mb-4">
              {notice && (
                <p className="text-xs font-medium text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2 mb-2">{notice}</p>
              )}
              {overviewRow('Kategori', 'category',
                savedSplitCount > 0 ? (
                  <span className="text-gray-500">Uppdelad — se Uppdelning</span>
                ) : curCategory ? (
                  <>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: curCategory.color || '#9CA3AF' }}></span>
                    <span className="truncate">{curCategory.name}</span>
                  </>
                ) : (
                  <span className="text-blue-600">Välj kategori</span>
                )
              )}
              {overviewRow('Namn', 'name', <span className="truncate">{displayName}</span>)}
              <button
                onClick={toggleTransfer}
                disabled={savingTransfer}
                className="w-full flex items-center py-3 px-2 text-left hover:bg-gray-50 rounded-lg border-b border-gray-100 disabled:opacity-50"
              >
                <span className="text-sm text-gray-500 w-28 flex-shrink-0">Överföring</span>
                <span className="flex-1 text-right text-sm font-medium">
                  {isTransfer
                    ? <span className="text-gray-800">Ja — mellan egna konton</span>
                    : <span className="text-gray-400 font-normal">Nej</span>}
                </span>
                <span className={`ml-3 w-10 h-6 rounded-full flex-shrink-0 relative transition-colors ${isTransfer ? 'bg-blue-600' : 'bg-gray-200'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${isTransfer ? 'left-[18px]' : 'left-0.5'}`}></span>
                </span>
              </button>
              {overviewRow('Uppdelning', 'split',
                savedSplitCount > 0
                  ? <span>{savedSplitCount} delar</span>
                  : <span className="text-gray-400 font-normal">Nej</span>
              )}
              {overviewRow('Taggar', 'tags',
                txTagIds.length > 0 ? (
                  <span className="flex flex-wrap gap-1 justify-end">
                    {allTags.filter(t => txTagIds.includes(t.id)).map(t => (
                      <span key={t.id} className="inline-flex items-center gap-1 text-xs font-medium border border-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color || '#0EA5E9' }}></span>
                        {t.name}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="text-gray-400 font-normal">Inga</span>
                )
              )}
              {overviewRow('Kvittning', 'refund',
                refundSummary
                  ? <span className="text-green-700 truncate">{refundSummary}</span>
                  : <span className="text-gray-400 font-normal">Ingen</span>
              )}
              {isWorkExpense && overviewRow('Kvitto', 'receipt',
                receipt ? (
                  <span className="inline-flex items-center gap-1.5">
                    <FileText size={13} className={receipt.hasFile ? 'text-green-600' : 'text-gray-400'} />
                    {receipt.hasFile ? 'Bifogad fil' : 'Bara mail'}
                  </span>
                ) : (
                  <span className="text-amber-700 font-normal">Saknas</span>
                )
              )}
              {overviewRow('Regler', 'rules',
                matchingRules.length > 0 ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Zap size={13} className="text-amber-500" />
                    {matchingRules.length === 1 ? '1 regel träffar' : `${matchingRules.length} regler träffar`}
                  </span>
                ) : (
                  <span className="text-gray-400 font-normal">Inga</span>
                )
              )}
            </div>
            <button
              onClick={onClose}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Stäng
            </button>
          </>
        )}

        {view === 'receipt' && (
          <div className="flex-1 overflow-y-auto">
            {receipt ? (
              <div className="rounded-lg border border-gray-200 px-3 py-2 mb-3 text-sm">
                <p className="font-medium text-gray-900">{receipt.vendor}</p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {[receipt.amount != null
                    ? `${receipt.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${receipt.currency || ''}`.trim()
                    : null, receipt.date].filter(Boolean).join(' · ')}
                  {receipt.confidence && ` · ${receipt.confidence}`}
                </p>
                {receipt.subject && <p className="text-gray-600 mt-2">{receipt.subject}</p>}
                {receipt.note && <p className="text-gray-500 text-xs mt-1">{receipt.note}</p>}
                {receipt.url && (
                  <a href={receipt.url} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-blue-600 hover:underline mt-2 block break-all">
                    Öppna kvittot hos {receipt.vendor}
                  </a>
                )}
                {!receipt.hasFile && (
                  <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                    Ingen bifogad fil — leverantören skickade kvittot i mailtexten.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-3">
                Inget kvitto är kopplat till den här dragningen än.
              </p>
            )}

            {receiptError && (
              <p className="text-sm text-amber-700 mb-3">
                <AlertCircle size={13} className="inline mr-1 -mt-0.5" />{receiptError}
              </p>
            )}

            <input
              ref={receiptInput}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) uploadReceipt(f);
              }}
            />
            <div className="space-y-2">
              <button
                onClick={() => receiptInput.current?.click()}
                disabled={receiptBusy}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                <Upload size={15} className="inline mr-1.5 -mt-0.5" />
                {receiptBusy ? 'Laddar upp…' : receipt?.hasFile ? 'Ersätt med egen fil' : 'Ladda upp kvitto'}
              </button>
              {receipt?.hasFile && (
                <button
                  onClick={downloadReceipt}
                  className="w-full border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  <Download size={15} className="inline mr-1.5 -mt-0.5" />Ladda ner
                </button>
              )}
            </div>
          </div>
        )}

        {view === 'category' && (
          <>
            <div className="flex-1 overflow-y-auto mb-3">
              <div className="flex flex-col">
                {categoryTree.map(cat => {
                  const isOpen = expandedCatGroups.has(cat.id);
                  const rowClass = (id: number) =>
                    `flex items-center flex-1 p-2.5 rounded-lg text-left text-sm font-medium transition-colors ${
                      selectedCategoryId === id ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`;
                  return (
                    <div key={cat.id} className="border-b border-gray-100 last:border-0">
                      <div className="flex items-center">
                        <button onClick={() => setSelectedCategoryId(cat.id)} className={rowClass(cat.id)}>
                          <span className="w-3 h-3 rounded-full mr-2.5 flex-shrink-0" style={{ backgroundColor: cat.color || '#9CA3AF' }}></span>
                          {cat.name}
                        </button>
                        {cat.children.length > 0 && (
                          <button
                            onClick={() => setExpandedCatGroups(prev => {
                              const next = new Set(prev);
                              if (next.has(cat.id)) next.delete(cat.id); else next.add(cat.id);
                              return next;
                            })}
                            className="p-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
                          >
                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        )}
                      </div>
                      {isOpen && cat.children.map(child => (
                        <div key={child.id} className="ml-4 pl-2 border-l-2 border-gray-100">
                          <button onClick={() => setSelectedCategoryId(child.id)} className={rowClass(child.id) + ' w-full'}>
                            <span className="w-2.5 h-2.5 rounded-full mr-2.5 flex-shrink-0" style={{ backgroundColor: child.color || '#9CA3AF' }}></span>
                            {child.name}
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {showNewCategory ? (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      value={newCatName}
                      onChange={e => setNewCatName(e.target.value)}
                      placeholder="Namn på ny kategori"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
                      autoFocus
                    />
                    <select
                      value={newCatParentId}
                      onChange={e => setNewCatParentId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white mb-2"
                    >
                      <option value="">Huvudkategori (egen hink)</option>
                      {categoryTree.map(c => (
                        <option key={c.id} value={c.id}>Underkategori till {c.name}</option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreateCategory}
                        disabled={creatingCat || !newCatName.trim()}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                      >
                        {creatingCat ? 'Skapar…' : 'Skapa'}
                      </button>
                      <button
                        onClick={() => setShowNewCategory(false)}
                        className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg text-sm font-semibold"
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewCategory(true)}
                    className="flex items-center p-2.5 mt-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Plus size={15} className="mr-2" />Ny kategori
                  </button>
                )}
              </div>
            </div>
            <label className="flex items-start text-sm text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={createRule}
                onChange={e => setCreateRule(e.target.checked)}
                className="mr-2 mt-0.5"
              />
              <span>Applicera på <strong>alla liknande</strong> transaktioner (skapar en regel som även gäller framtida köp)</span>
            </label>
            {createRule && (
              <>
                <input
                  type="text"
                  value={ruleText}
                  onChange={e => setRuleText(e.target.value)}
                  placeholder="Text som matchar (kortas gärna ner, t.ex. 'ica')"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
                />
                <input
                  type="text"
                  value={renameTo}
                  onChange={e => setRenameTo(e.target.value)}
                  placeholder="Eget namn i listorna (valfritt, t.ex. 'Farmors Café')"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
                />
              </>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setView('overview')}
                disabled={saving}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Tillbaka
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={saving || !canSaveCategory}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Sparar…' : 'Spara'}
              </button>
            </div>
          </>
        )}

        {view === 'name' && (
          <>
            <div className="flex-1 mb-3">
              <label className="block text-xs text-gray-500 mb-1">Namn för den här transaktionen</label>
              <input
                type="text"
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                placeholder="T.ex. Kylskåp (Swish till Eva Westerlund)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2">
                Gäller bara den här transaktionen. Vill du döpa om alla liknande — skapa en regel under Kategori.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setEditDescription(displayName); setView('overview'); }}
                disabled={saving}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Tillbaka
              </button>
              <button
                onClick={handleSaveName}
                disabled={saving || !canSaveName}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Sparar…' : 'Spara'}
              </button>
            </div>
          </>
        )}

        {view === 'split' && (
          <>
            <div className="flex-1 overflow-y-auto mb-3">
              <p className="text-xs text-gray-500 mb-2">
                Dela upp beloppet i delar med varsin kategori — t.ex. en faktura som är både hosting och domän.
              </p>
              {splitParts.map((part, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-2.5 mb-2">
                  <div className="flex gap-2 mb-1.5">
                    <select
                      value={part.categoryId}
                      onChange={e => setSplitParts(prev => prev.map((p, i) => i === idx ? { ...p, categoryId: e.target.value } : p))}
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white min-w-0"
                    >
                      <option value="">Välj kategori…</option>
                      {categoryOptions.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={part.amount}
                      onChange={e => setSplitParts(prev => prev.map((p, i) => i === idx ? { ...p, amount: e.target.value } : p))}
                      placeholder="0"
                      className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right"
                    />
                    {splitParts.length > 2 && (
                      <button
                        onClick={() => setSplitParts(prev => prev.filter((_, i) => i !== idx))}
                        className="p-1.5 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={part.label}
                    onChange={e => setSplitParts(prev => prev.map((p, i) => i === idx ? { ...p, label: e.target.value } : p))}
                    placeholder="Eget namn på delen (valfritt, t.ex. 'Domän letscharge.se')"
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                  />
                </div>
              ))}
              <div className="flex items-center justify-between mb-1">
                <button
                  onClick={() => setSplitParts(prev => [...prev, { categoryId: '', amount: splitRemainder > 0 ? String(splitRemainder) : '', label: '' }])}
                  className="flex items-center text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg px-2 py-1.5"
                >
                  <Plus size={14} className="mr-1" />Lägg till del
                </button>
                <span className={`text-sm font-medium ${Math.abs(splitRemainder) <= 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
                  {Math.abs(splitRemainder) <= 0.01
                    ? 'Hela beloppet fördelat'
                    : splitRemainder > 0
                      ? `${splitRemainder.toLocaleString('sv-SE')} kr kvar att fördela`
                      : `${Math.abs(splitRemainder).toLocaleString('sv-SE')} kr för mycket`}
                </span>
              </div>
              {savedSplitCount > 0 && (
                <button
                  onClick={handleRemoveSplit}
                  disabled={saving}
                  className="flex items-center text-sm text-red-600 hover:bg-red-50 rounded-lg px-2 py-1.5 mt-1"
                >
                  <Trash2 size={14} className="mr-1" />Ta bort uppdelningen
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setView('overview')}
                disabled={saving}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Tillbaka
              </button>
              <button
                onClick={handleSaveSplit}
                disabled={saving || !canSaveSplit}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Sparar…' : 'Dela upp'}
              </button>
            </div>
          </>
        )}

        {view === 'tags' && (
          <>
            <div className="flex-1 overflow-y-auto mb-3">
              <p className="text-xs text-gray-500 mb-2">Taggar följer transaktionen över kategorigränser — t.ex. Landstället, Bilen, Semester. Sparas direkt.</p>
              <div className="flex flex-wrap gap-1.5 items-center">
                {allTags.map(tag => {
                  const active = txTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                        active ? 'border-transparent text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                      style={active ? { backgroundColor: tag.color || '#0EA5E9' } : undefined}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? 'rgba(255,255,255,0.8)' : (tag.color || '#0EA5E9') }}></span>
                      {tag.name}
                    </button>
                  );
                })}
                {showNewTag ? (
                  <span className="inline-flex items-center gap-1">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={e => setNewTagName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleCreateTag(); }}
                      placeholder="Ny tagg…"
                      className="border border-gray-300 rounded-full px-2.5 py-1 text-xs w-28"
                      autoFocus
                    />
                    <button onClick={handleCreateTag} disabled={!newTagName.trim()} className="text-xs font-medium text-blue-600 px-1">OK</button>
                    <button onClick={() => { setShowNewTag(false); setNewTagName(''); }} className="text-gray-400 px-1"><X size={12} /></button>
                  </span>
                ) : (
                  <button
                    onClick={() => setShowNewTag(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 px-2 py-1 rounded-full hover:bg-blue-50"
                  >
                    <Plus size={12} />Ny tagg
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={() => setView('overview')}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Tillbaka
            </button>
          </>
        )}

        {view === 'refund' && (
          <>
            <div className="flex-1 overflow-y-auto mb-3">
              {!isIncome && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1.5">Retur eller taget från sparande? Kvitta inbetalningen mot det här köpet så räknas nettot i budgeten. Sparas direkt.</p>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {linkedRefunds.map(r => (
                      <span key={r.id} className="inline-flex items-center gap-1.5 text-xs font-medium bg-green-50 border border-green-200 text-green-700 px-2.5 py-1 rounded-full">
                        +{formatAmount(r.amount)} · {r.date ? new Date(r.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : ''}
                        <button onClick={() => unlinkRefund(r.id)} disabled={linkingRefund} className="text-green-600 hover:text-green-800">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    {refundSuggestions.map(s => (
                      <button
                        key={s.id}
                        onClick={() => linkRefund(s)}
                        disabled={linkingRefund}
                        className="inline-flex items-center gap-1.5 text-xs font-medium border border-dashed border-gray-300 text-gray-600 px-2.5 py-1 rounded-full hover:border-green-400 hover:text-green-700 hover:bg-green-50 disabled:opacity-50"
                        title="Trolig retur — klicka för att kvitta mot köpet"
                      >
                        <Plus size={11} />
                        +{formatAmount(Math.abs(s.amount))} · {s.description} · {s.date ? new Date(s.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : ''}
                      </button>
                    ))}
                    {!showRefundPicker && (
                      <button
                        onClick={() => setShowRefundPicker(true)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 px-2 py-1 rounded-full hover:bg-blue-50"
                      >
                        <Plus size={12} />Välj inbetalning
                      </button>
                    )}
                  </div>
                  {linkedRefunds.reduce((s, r) => s + r.amount, 0) > Math.abs(tx.amount) + 0.01 && (
                    <p className="text-xs text-amber-600 mt-1.5">
                      Inbetalningarna är {formatAmount(linkedRefunds.reduce((s, r) => s + r.amount, 0) - Math.abs(tx.amount))} mer än köpet — överskottet räknas inte mot köpet utan bokförs där inbetalningen hör hemma.
                    </p>
                  )}
                  {showRefundPicker && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden mt-1.5">
                      <input
                        type="text"
                        value={refundSearch}
                        onChange={e => setRefundSearch(e.target.value)}
                        placeholder="Sök bland inbetalningar…"
                        className="w-full px-3 py-2 text-sm border-b border-gray-200 outline-none"
                        autoFocus
                      />
                      <div className="max-h-40 overflow-y-auto">
                        {allTxs
                          .filter(t => t.type === 'INCOME' && !t.isTransfer && !t.refundsTransactionId)
                          .filter(t => !refundSearch.trim() || t.description.toLowerCase().includes(refundSearch.toLowerCase()))
                          .slice(0, 30)
                          .map(t => (
                            <button
                              key={t.id}
                              onClick={() => { linkRefund(t); setShowRefundPicker(false); }}
                              disabled={linkingRefund}
                              className="w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
                            >
                              <span className="flex items-center min-w-0">
                                <span className="text-xs text-gray-400 w-12 flex-shrink-0">
                                  {t.date ? new Date(t.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : ''}
                                </span>
                                <span className="text-gray-700 truncate">{t.description}</span>
                              </span>
                              <span className="text-green-600 ml-2 flex-shrink-0">+{formatAmount(Math.abs(t.amount))}</span>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isIncome && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1.5">Retur eller uttag från sparande? Kvitta mot köpet det hör till. Sparas direkt.</p>
                  {refundTargetId ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-green-50 border border-green-200 text-green-700 px-2.5 py-1 rounded-full">
                      Kvittas mot {refundTargetLabel || 'valt köp'}
                      <button onClick={unlinkFromPurchase} disabled={linkingRefund} className="text-green-600 hover:text-green-800">
                        <X size={12} />
                      </button>
                    </span>
                  ) : showRefundPicker ? (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <input
                        type="text"
                        value={refundSearch}
                        onChange={e => setRefundSearch(e.target.value)}
                        placeholder="Sök bland köp…"
                        className="w-full px-3 py-2 text-sm border-b border-gray-200 outline-none"
                        autoFocus
                      />
                      <div className="max-h-40 overflow-y-auto">
                        {recentExpenses
                          .filter(e => !refundSearch.trim() || e.description.toLowerCase().includes(refundSearch.toLowerCase()))
                          .slice(0, 30)
                          .map(e => (
                            <button
                              key={e.id}
                              onClick={() => { linkToPurchase(e); setShowRefundPicker(false); }}
                              disabled={linkingRefund}
                              className="w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
                            >
                              <span className="flex items-center min-w-0">
                                <span className="text-xs text-gray-400 w-12 flex-shrink-0">
                                  {e.date ? new Date(e.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : ''}
                                </span>
                                <span className="text-gray-700 truncate">{e.description}</span>
                              </span>
                              <span className="text-gray-500 ml-2 flex-shrink-0">{formatAmount(Math.abs(e.amount))}</span>
                            </button>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowRefundPicker(true)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 px-2 py-1 rounded-full hover:bg-blue-50"
                    >
                      <Plus size={12} />Välj köp att kvitta mot
                    </button>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setView('overview')}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Tillbaka
            </button>
          </>
        )}

        {view === 'rules' && (
          <>
            <div className="flex-1 overflow-y-auto mb-3">
              {matchingRules.length === 0 ? (
                <div className="text-center py-8">
                  <Zap size={22} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">Inga regler påverkar den här transaktionen.</p>
                  <p className="text-xs text-gray-400 mt-1.5 max-w-xs mx-auto">
                    Skapa en under Kategori med "Applicera på alla liknande transaktioner".
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mb-2">
                    Regler körs automatiskt på nya transaktioner från banken — även namnbyten.
                  </p>
                  {matchingRules.map(r => {
                    const conditions: string[] = [];
                    if (r.textMatch) conditions.push(`innehåller "${r.textMatch}"`);
                    if (r.amountMin !== null && r.amountMax !== null && r.amountMin === r.amountMax) {
                      conditions.push(`exakt ${formatAmount(r.amountMin)}`);
                    } else {
                      if (r.amountMin !== null) conditions.push(`minst ${formatAmount(r.amountMin)}`);
                      if (r.amountMax !== null) conditions.push(`högst ${formatAmount(r.amountMax)}`);
                    }
                    const overridden = (r.category && r.id !== effectiveCategoryRuleId) ||
                      (!r.category && r.renameTo && r.id !== effectiveRenameRuleId);
                    return (
                      <div key={r.id} className={`border border-gray-200 rounded-lg p-3 mb-2 ${overridden ? 'opacity-50' : ''}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="inline-flex items-center text-sm font-medium text-gray-800 min-w-0">
                            <Zap size={13} className="mr-1.5 text-amber-500 flex-shrink-0" />
                            <span className="truncate">{conditions.join(' · ')}</span>
                          </span>
                          {overridden && (
                            <span className="text-[10px] uppercase tracking-wide text-gray-400 ml-2 flex-shrink-0">överkörd</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {r.category && (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.categoryColor || '#9CA3AF' }}></span>
                              {r.category}
                            </span>
                          )}
                          {r.renameTo && (
                            <span className="inline-flex items-center text-xs font-medium bg-blue-50 border border-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                              döps till "{r.renameTo}"
                            </span>
                          )}
                          {r.isRecurring && (
                            <span className="inline-flex items-center text-xs font-medium bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-full">
                              återkommande{r.intervalMonths && r.intervalMonths > 1 ? ` var ${r.intervalMonths}:e månad` : ' varje månad'}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            <button
              onClick={() => setView('overview')}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Tillbaka
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CategorizeModal;
